import React, { useEffect, useMemo, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Package, Radio, DollarSign, Truck, CircleDot } from "lucide-react";

const INITIAL_INVENTORY = [
  { sku: "PVC-4IN-10FT", name: 'PVC Pipe 4" x 10\'', bin: "A-12", qty: 42, reorder: 30, unit: 8.4 },
  { sku: "COPPER-3-4", name: 'Copper 3/4"', bin: "B-04", qty: 6, reorder: 12, unit: 24.9 },
  { sku: "PEX-1-2-500", name: "PEX 1/2\" 500ft roll", bin: "C-21", qty: 18, reorder: 10, unit: 189.0 },
  { sku: "WIRE-12-2-250", name: "Romex 12/2 250ft", bin: "A-07", qty: 3, reorder: 8, unit: 142.0 },
  { sku: "FUSE-30A", name: "30A Fuse (pack)", bin: "D-14", qty: 88, reorder: 40, unit: 3.2 },
];

const FIELD_LOG = [
  { t: "07:42", who: "Marcus · Truck 3", msg: "Pulled 8x PVC-4IN-10FT for Kingston site", type: "draw" },
  { t: "08:11", who: "Jasmine · Shop", msg: "Price check → Ferguson: Copper 3/4\" $22.10 (▼11%)", type: "price" },
  { t: "08:34", who: "Devon · Truck 1", msg: "Route re-cued: Hilton bldg 6 → 3 → 8", type: "route" },
  { t: "09:02", who: "SYS", msg: "Low-stock alert: WIRE-12-2-250 below reorder", type: "alert" },
  { t: "09:15", who: "William · Owner", msg: "Approved PO #4482 · $612.40 · Ferguson", type: "po" },
  { t: "09:41", who: "Marcus · Truck 3", msg: "Job 218 marked complete · 4.2hrs · $1,180", type: "job" },
];

const SUPPLIERS = [
  { name: "Ferguson", price: 22.10, best: true },
  { name: "Home Depot Pro", price: 24.90, best: false },
  { name: "Lowe's ProDesk", price: 25.40, best: false },
  { name: "Local Supply Co", price: 26.75, best: false },
];

const badgeStyle = (t) => {
  switch (t) {
    case "draw": return "text-nex-silver border-nex-chrome/40";
    case "price": return "text-nex-gold border-nex-gold/50";
    case "route": return "text-cyan-300 border-cyan-500/40";
    case "alert": return "text-red-400 border-red-500/50";
    case "po": return "text-emerald-300 border-emerald-500/40";
    case "job": return "text-nex-silver border-white/20";
    default: return "text-nex-chrome border-white/10";
  }
};

const LiveDemo = ({ embedded = false }) => {
  const [inventory] = useState(INITIAL_INVENTORY);
  const [tickerIdx, setTickerIdx] = useState(0);
  const [selectedSupplier, setSelectedSupplier] = useState(0);

  useEffect(() => {
    const t = setInterval(() => setTickerIdx((i) => (i + 1) % FIELD_LOG.length), 2400);
    return () => clearInterval(t);
  }, []);

  const stats = useMemo(() => {
    const totalValue = inventory.reduce((s, x) => s + x.qty * x.unit, 0);
    const lowStock = inventory.filter((x) => x.qty < x.reorder).length;
    return { totalValue, lowStock, skuCount: inventory.length };
  }, [inventory]);

  const Shell = ({ children }) => embedded ? <>{children}</> : (
    <section id="demo" data-testid="live-demo-section" className="relative py-28 lg:py-36 bg-[#0A0F1C]">
      <div className="absolute inset-0 blueprint-bg opacity-40 pointer-events-none" />
      <div className="w-full mx-auto px-6 md:px-10 lg:px-16 xl:px-24 2xl:px-32 relative">
        <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-6 mb-12">
          <div>
            <div className="flex items-center gap-4 mb-5">
              <div className="h-px w-8 bg-nex-gold" />
              <span className="mono-label">// LIVE ENGINE · FLOW.v3.2</span>
            </div>
            <h2 className="serif-head text-4xl md:text-6xl">
              Touch the <span className="italic text-nex-gold">Flow Engine.</span>
              <br /> Live. Right now.
            </h2>
          </div>
          <p className="mono-body max-w-md text-[13px] leading-relaxed">
            &gt; This is a scaled-down build of the real engine we deployed for a specialty trades company. Inventory, field ledger, and supplier price-check — all in one operational surface.
          </p>
        </div>
        {children}
        <p className="mono-body text-center mt-8 text-[12px] max-w-2xl mx-auto">
          &gt; This is a demo replica. Your engine ships with your brand colors, your SKUs, your workflow — deployed on your infrastructure inside 14 days.
        </p>
      </div>
    </section>
  );

  return (
    <Shell>
      {/* Dashboard shell */}
      <div className="card-industrial corner-tick relative overflow-hidden">
          {/* window chrome */}
          <div className="flex items-center justify-between px-5 py-3 border-b border-white/10 bg-[#0B0F19]">
            <div className="flex items-center gap-3">
              <div className="flex gap-1.5">
                <span className="w-2.5 h-2.5 rounded-full bg-red-500/60" />
                <span className="w-2.5 h-2.5 rounded-full bg-yellow-500/60" />
                <span className="w-2.5 h-2.5 rounded-full bg-emerald-500/60" />
              </div>
              <span className="mono-body text-[11px]">nex://flow-engine/wired-sc/ops</span>
            </div>
            <span className="mono-body text-[11px] flex items-center gap-2">
              <CircleDot size={10} className="text-nex-gold animate-pulse-gold" /> LIVE
            </span>
          </div>

          <div className="grid lg:grid-cols-12">
            {/* LEFT: KPIs + Inventory */}
            <div className="lg:col-span-8 border-r border-white/10">
              {/* KPIs */}
              <div className="grid grid-cols-3 border-b border-white/10">
                {[
                  { k: "SKUs Tracked", v: stats.skuCount, sub: "active bins" },
                  { k: "Inventory Value", v: `$${stats.totalValue.toFixed(0)}`, sub: "on-hand USD" },
                  { k: "Low Stock", v: stats.lowStock, sub: "below reorder", warn: stats.lowStock > 0 },
                ].map((kpi, i) => (
                  <div key={i} data-testid={`demo-kpi-${i}`} className={`p-6 ${i > 0 ? "border-l border-white/10" : ""}`}>
                    <div className="mono-label mb-2">{kpi.k}</div>
                    <div className={`serif-head text-3xl ${kpi.warn ? "text-red-400" : "text-nex-silver"}`}>{kpi.v}</div>
                    <div className="mono-body text-[10px] mt-1">{kpi.sub}</div>
                  </div>
                ))}
              </div>

              {/* Inventory */}
              <div className="p-6">
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-3">
                    <Package size={16} className="text-nex-gold" />
                    <span className="mono-label">// Inventory Ledger</span>
                  </div>
                  <span className="mono-body text-[10px]">bin sync · &lt; 200ms</span>
                </div>

                <div className="border border-white/10">
                  <div className="grid grid-cols-12 mono-body text-[10px] uppercase tracking-widest px-4 py-2 bg-[#0B0F19] border-b border-white/10">
                    <div className="col-span-4">SKU</div>
                    <div className="col-span-2">Bin</div>
                    <div className="col-span-2 text-right">Qty</div>
                    <div className="col-span-2 text-right">Reorder</div>
                    <div className="col-span-2 text-right">Unit</div>
                  </div>
                  {inventory.map((row) => {
                    const low = row.qty < row.reorder;
                    return (
                      <div
                        key={row.sku}
                        data-testid={`inv-row-${row.sku}`}
                        className={`grid grid-cols-12 px-4 py-3 text-[13px] border-b border-white/5 last:border-b-0 ${low ? "bg-red-500/5" : ""}`}
                      >
                        <div className="col-span-4 text-nex-silver">
                          <div className="font-medium">{row.name}</div>
                          <div className="mono-body text-[10px]">{row.sku}</div>
                        </div>
                        <div className="col-span-2 mono-body text-nex-chrome self-center">{row.bin}</div>
                        <div className={`col-span-2 text-right font-mono self-center ${low ? "text-red-400 font-bold" : "text-nex-silver"}`}>
                          {row.qty}
                        </div>
                        <div className="col-span-2 text-right font-mono text-nex-muted self-center">{row.reorder}</div>
                        <div className="col-span-2 text-right font-mono text-nex-silver self-center">${row.unit.toFixed(2)}</div>
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Supplier price check */}
              <div className="p-6 border-t border-white/10">
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-3">
                    <DollarSign size={16} className="text-nex-gold" />
                    <span className="mono-label">// Supplier Price Check · Copper 3/4"</span>
                  </div>
                  <span className="mono-body text-[10px]">avg $24.79</span>
                </div>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  {SUPPLIERS.map((s, i) => (
                    <button
                      key={s.name}
                      data-testid={`supplier-${i}`}
                      onClick={() => setSelectedSupplier(i)}
                      className={`text-left p-4 border transition-colors ${
                        selectedSupplier === i
                          ? "border-nex-gold bg-nex-gold/10"
                          : "border-white/10 hover:border-white/30 bg-[#0B0F19]"
                      }`}
                    >
                      <div className="mono-body text-[10px] uppercase tracking-widest text-nex-chrome">{s.name}</div>
                      <div className={`serif-head text-2xl mt-1 ${s.best ? "text-nex-gold" : "text-nex-silver"}`}>
                        ${s.price.toFixed(2)}
                      </div>
                      {s.best && <div className="mono-body text-[10px] text-nex-gold mt-1">◆ BEST · save 11%</div>}
                    </button>
                  ))}
                </div>
              </div>
            </div>

            {/* RIGHT: Field-to-shop log */}
            <div className="lg:col-span-4">
              <div className="p-6 border-b border-white/10 flex items-center gap-3">
                <Radio size={16} className="text-nex-gold" />
                <span className="mono-label">// Field ⇄ Shop Ledger</span>
              </div>
              <div className="p-6 space-y-3 max-h-[540px] overflow-hidden">
                {FIELD_LOG.map((log, i) => {
                  const active = i === tickerIdx;
                  return (
                    <AnimatePresence mode="wait" key={log.t + log.who}>
                      <motion.div
                        initial={{ opacity: 0, x: -8 }}
                        animate={{ opacity: active ? 1 : 0.5, x: 0 }}
                        transition={{ duration: 0.4 }}
                        className={`border-l-2 pl-4 py-1 transition-colors ${
                          active ? "border-nex-gold" : "border-white/10"
                        }`}
                      >
                        <div className="flex items-center gap-3 mb-1">
                          <span className="mono-body text-[10px] text-nex-gold">{log.t}</span>
                          <span className={`mono-body text-[9px] uppercase tracking-widest px-2 py-0.5 border ${badgeStyle(log.type)}`}>
                            {log.type}
                          </span>
                        </div>
                        <div className="mono-body text-[11px] text-nex-chrome">{log.who}</div>
                        <div className="text-nex-silver text-sm leading-snug mt-1">{log.msg}</div>
                      </motion.div>
                    </AnimatePresence>
                  );
                })}
              </div>
              <div className="p-4 border-t border-white/10 flex items-center gap-2">
                <Truck size={12} className="text-nex-chrome" />
                <span className="mono-body text-[10px]">3 trucks · 12 techs · 218 jobs YTD</span>
              </div>
            </div>
          </div>
        </div>
    </Shell>
  );
};

export default LiveDemo;
